const SELECTORS = {
    root: ".site-popover",
    opener: ".site-popover__opener",
    dropdown: ".site-popover__dropdown",
};

// on the root it rotates the opener caret; on the dropdown it drives the fade/slide in
const ACTIVE_CLASS = "active";

const OFFSET = 4;        // gap between the opener and the dropdown
const VIEWPORT_GAP = 8;  // smallest distance kept from the viewport edges
const CLOSE_DELAY = 150; // matches --transition-fast (.1s) plus a little slack

// horizontal alignment relative to the opener, declared via site-popover__{direction}
const ALIGNMENTS = ["left", "middle", "right"];

export function popover() {
    // every popover currently open, so scroll/resize can keep them glued to the opener
    const openPopovers = new Set();

    // once portaled the dropdown lives in <body>, so cache the reference on the root
    function getDropdown(root) {
        if (!root._popoverDropdown) {
            root._popoverDropdown = root.querySelector(SELECTORS.dropdown);
        }
        return root._popoverDropdown;
    }

    function getAlignment(root, dropdown) {
        const found = ALIGNMENTS.find(name =>
            root.classList.contains(`site-popover__${name}`) ||
            dropdown.classList.contains(`site-popover__${name}`)
        );
        return found || "left";
    }

    // opt-in: when set, the dropdown matches the opener's width exactly
    function matchesParentWidth(root, dropdown) {
        return root.hasAttribute("data-parent-width") ||
            dropdown.hasAttribute("data-parent-width");
    }

    // place the dropdown next to its opener, flipping/clamping to stay on screen
    function position(root) {
        const opener = root.querySelector(SELECTORS.opener);
        const dropdown = getDropdown(root);
        if (!opener || !dropdown) return;

        const openerRect = opener.getBoundingClientRect();

        // lock the width to the opener before measuring, so clamping uses the final size
        if (matchesParentWidth(root, dropdown)) {
            const width = `${openerRect.width}px`;
            dropdown.style.width = width;
            dropdown.style.minWidth = width;
            dropdown.style.maxWidth = width;
        }

        const ddRect = dropdown.getBoundingClientRect();
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;

        // vertical: open downwards, flip up only when it doesn't fit below
        const spaceBelow = vh - openerRect.bottom;
        const spaceAbove = openerRect.top;
        const needed = ddRect.height + OFFSET + VIEWPORT_GAP;
        const placeBelow = spaceBelow >= needed || spaceBelow >= spaceAbove;

        const top = placeBelow
            ? openerRect.bottom + OFFSET
            : openerRect.top - OFFSET - ddRect.height;

        // horizontal: honour the requested alignment, then clamp inside the viewport
        const align = getAlignment(root, dropdown);
        let left;
        if (align === "right") {
            left = openerRect.right - ddRect.width;
        } else if (align === "middle") {
            left = openerRect.left + (openerRect.width - ddRect.width) / 2;
        } else {
            left = openerRect.left;
        }

        const maxLeft = vw - VIEWPORT_GAP - ddRect.width;
        left = Math.min(Math.max(left, VIEWPORT_GAP), Math.max(VIEWPORT_GAP, maxLeft));

        dropdown.style.top = `${Math.round(top)}px`;
        dropdown.style.left = `${Math.round(left)}px`;
        dropdown.dataset.placement = placeBelow ? "bottom" : "top";
    }

    // send the dropdown to the end of <body>, leaving a placeholder to move it back
    function portal(root) {
        const dropdown = getDropdown(root);
        if (!dropdown || dropdown._portaled) return;
        const placeholder = document.createComment("site-popover-dropdown");
        dropdown.before(placeholder);
        dropdown._placeholder = placeholder;
        dropdown._portaled = true;
        document.body.appendChild(dropdown);
    }

    function restore(root) {
        const dropdown = getDropdown(root);
        if (!dropdown || !dropdown._portaled) return;
        const placeholder = dropdown._placeholder;
        if (placeholder && placeholder.parentNode) {
            placeholder.replaceWith(dropdown);
        } else {
            root.appendChild(dropdown);
        }
        dropdown._placeholder = null;
        dropdown._portaled = false;
        dropdown.style.top = "";
        dropdown.style.left = "";
        dropdown.style.width = "";
        dropdown.style.minWidth = "";
        dropdown.style.maxWidth = "";
        delete dropdown.dataset.placement;
    }

    function open(root) {
        const dropdown = getDropdown(root);
        if (!dropdown) return;

        clearTimeout(dropdown._closeTimer);
        portal(root);
        position(root);

        // reflow so the transition runs from the hidden state instead of snapping in
        void dropdown.offsetWidth;

        root.classList.add(ACTIVE_CLASS);
        dropdown.classList.add(ACTIVE_CLASS);
        openPopovers.add(root);
    }

    function close(root) {
        const dropdown = getDropdown(root);
        root.classList.remove(ACTIVE_CLASS);
        openPopovers.delete(root);
        if (!dropdown) return;

        dropdown.classList.remove(ACTIVE_CLASS);

        // keep it in the portal until the fade-out ends, then move it back home
        clearTimeout(dropdown._closeTimer);
        dropdown._closeTimer = setTimeout(() => {
            if (!dropdown.classList.contains(ACTIVE_CLASS)) restore(root);
        }, CLOSE_DELAY);
    }

    // close every open popover, optionally skipping one we want to keep
    function closeAll(except = null) {
        [...openPopovers].forEach(root => {
            if (root !== except) close(root);
        });
    }

    function toggle(root) {
        const isOpen = openPopovers.has(root);
        closeAll(root);
        if (isOpen) close(root);
        else open(root);
    }

    // single delegated listener: handles every popover on the page,
    // including ones added to the DOM later
    document.addEventListener("click", event => {
        const opener = event.target.closest(SELECTORS.opener);

        if (opener) {
            toggle(opener.closest(SELECTORS.root));
            return;
        }

        // a click inside an open dropdown (now living in <body>) must not close it
        if (event.target.closest(SELECTORS.dropdown)) return;

        // any other click closes everything
        closeAll();
    });

    // Escape closes the open popover(s)
    document.addEventListener("keydown", event => {
        if (event.key === "Escape") closeAll();
    });

    // keep open popovers attached to their openers as the page scrolls or resizes
    function reposition() {
        openPopovers.forEach(position);
    }
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
}
